import { useState, useEffect, useRef } from 'react';
import { useTestStore, type TestId } from '@/store/useTestStore';
import { t } from '@/i18n/translations';
import { TestCard } from '@/components/TestCard';
import { MetricBox } from '@/components/MetricBox';
import { fmtInt, clamp } from '@/utils/format';
import { useFpsMeter } from '@/hooks/useFpsMeter';
import { qualityFromFps, scoreFromFps } from '@/utils/scoring';

interface Props { autoRun: boolean; autoNonce: number; }

export function Canvas2DTest({ autoRun, autoNonce }: Props) {
  const { language, reportResult, onAutoDone } = useTestStore();
  const [running, setRunning] = useState(false);
  const [count, setCount] = useState(2200);
  const [drawCalls, setDrawCalls] = useState(0);
  const fps = useFpsMeter(running);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const rafRef = useRef(0);
  const partsRef = useRef<{ x: number; y: number; vx: number; vy: number; r: number }[]>([]);
  const autoDoneRef = useRef(false);
  const runningRef = useRef(false);

  function resizeCanvas(canvas: HTMLCanvasElement) {
    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    const w = Math.max(1, Math.floor(rect.width * dpr));
    const h = Math.max(1, Math.floor(rect.height * dpr));
    if (canvas.width !== w || canvas.height !== h) { canvas.width = w; canvas.height = h; }
    return { w, h };
  }

  function start() {
    autoDoneRef.current = false;
    runningRef.current = true;
    setRunning(true);
    const canvas = canvasRef.current;
    if (!canvas) return;
    const info = resizeCanvas(canvas);
    const n = clamp(parseInt(String(count), 10) || 2200, 800, 8000);
    setCount(n);

    let seed = 987654321;
    function rnd() { seed ^= seed << 13; seed ^= seed >>> 17; seed ^= seed << 5; return (seed >>> 0) / 4294967295; }
    const parts = new Array(n);
    for (let i = 0; i < n; i++) {
      parts[i] = { x: rnd() * info.w, y: rnd() * info.h, vx: (rnd() - 0.5) * 1.8, vy: (rnd() - 0.5) * 1.8, r: 1 + (i % 3) };
    }
    partsRef.current = parts;

    function loop() {
      if (!runningRef.current) return;
      const ctx = canvas!.getContext('2d', { alpha: false });
      if (!ctx) return;
      const { w, h } = resizeCanvas(canvas!);
      ctx.fillStyle = 'rgba(0,0,0,0.35)';
      ctx.fillRect(0, 0, w, h);
      ctx.fillStyle = 'rgba(100,181,255,0.85)';
      const p = partsRef.current;
      for (let i = 0; i < p.length; i++) {
        const pt = p[i];
        pt.x += pt.vx; pt.y += pt.vy;
        if (pt.x < 0) { pt.x = 0; pt.vx *= -1; }
        if (pt.x > w) { pt.x = w; pt.vx *= -1; }
        if (pt.y < 0) { pt.y = 0; pt.vy *= -1; }
        if (pt.y > h) { pt.y = h; pt.vy *= -1; }
        ctx.fillRect(pt.x, pt.y, pt.r, pt.r);
      }
      setDrawCalls(p.length);
      rafRef.current = requestAnimationFrame(loop);
    }
    rafRef.current = requestAnimationFrame(loop);
  }

  function stop() {
    runningRef.current = false;
    setRunning(false);
    cancelAnimationFrame(rafRef.current);
    const sc = Math.round(scoreFromFps(fps));
    const testId: TestId = 'tv-canvas2d';
    reportResult(testId, { score: sc, primary: `FPS ${fps.toFixed(1)} · draws ~${Math.round(drawCalls)}`, details: { fps, drawCalls } });
    if (autoRun && !autoDoneRef.current) { autoDoneRef.current = true; onAutoDone(testId); }
  }

  function reset() { stop(); setCount(2200); setDrawCalls(0); }

  useEffect(() => {
    if (!autoRun) return;
    setCount(2200);
    start();
    const timer = setTimeout(() => stop(), 6000);
    return () => clearTimeout(timer);
  }, [autoRun, autoNonce]);

  const q = qualityFromFps(fps);

  return (
    <TestCard title={t('tv.canvas.title', language)} description={t('tv.canvas.desc', language)}>
      <div className="flex flex-wrap gap-2.5 items-center">
        <button className="btn btn-primary" onClick={start} disabled={running || autoRun}>{t('btn.start', language)}</button>
        <button className="btn btn-danger" onClick={stop} disabled={!running || autoRun}>{t('btn.stop', language)}</button>
        <button className="btn" onClick={reset} disabled={autoRun}>{t('btn.reset', language)}</button>
        <div className="flex-1" />
        <div className="field">
          <div className="label">{t('label.particles', language)}</div>
          <input value={count} disabled={running || autoRun} onChange={(e) => setCount(Number(e.target.value))} inputMode="numeric" className="input w-24" />
        </div>
      </div>
      <div className="grid grid-cols-4 gap-2.5 mt-3 max-lg:grid-cols-2 max-sm:grid-cols-1">
        <MetricBox label={t('metric.fps', language)} value={running ? fps.toFixed(1) : '—'} hint={t('hint.fpsGood', language)} />
        <MetricBox label={t('metric.quality', language)} value={running ? q.label : '—'} />
        <MetricBox label={t('metric.drawCalls', language)} value={running ? fmtInt(drawCalls) : '—'} />
        <MetricBox label="Notes" value="2D raster" />
      </div>
      <div className="h-px bg-border my-3.5" />
      <canvas ref={canvasRef} className="test-canvas border border-border rounded-2xl bg-black/20" />
    </TestCard>
  );
}
